/**
 * T975 — posthog-js remembers its last pageview across `reset()` and stamps it on what follows:
 * `$pageview_id` on every other event, `$prev_pageview_*` on the next pageview. After a new
 * identity (a re-grant in the page, an account switch, a sign-out) those would join it to the
 * one before. `before_send` passes every event through `keepToIdentity`, which drops any
 * reference to a pageview the current identity did not send.
 * Lightweight on purpose, like `held-page-context.ts`: no SDK import.
 */
let sentPageviews = new Set<unknown>();

/** A new identity starts with no pageview of its own. */
export function startIdentityScope(): void {
  sentPageviews = new Set();
}

type EventLike = { event?: string; properties?: Record<string, unknown> } | null | undefined;

export function keepToIdentity<E extends EventLike>(event: E): E {
  const properties = event?.properties;
  if (!properties) return event;
  if ("$prev_pageview_id" in properties && !sentPageviews.has(properties.$prev_pageview_id)) {
    for (const key of Object.keys(properties)) {
      if (key.startsWith("$prev_pageview_")) delete properties[key];
    }
  }
  if (event.event === "$pageview") sentPageviews.add(properties.$pageview_id);
  else if ("$pageview_id" in properties && !sentPageviews.has(properties.$pageview_id)) delete properties.$pageview_id;
  return event;
}
